import React, { useState } from 'react';
import { FaFileUpload, FaDownload, FaCheckCircle, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import api from '../api';

const ImportarFichasModal = ({ fecharModal, recarregar }) => {
  const [arquivo, setArquivo] = useState(null);
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleArquivoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setArquivo(file);
      setResultado(null);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        setArquivo(file);
        setResultado(null);
      } else {
        alert('Por favor, envie apenas arquivos Excel (.xlsx ou .xls)');
      }
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await api.get('/fichas/api/fichas-tecnicas/template-excel', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_fichas_tecnicas.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erro ao baixar template');
      console.error(err);
    }
  };

  const handleImportar = async () => {
    if (!arquivo) {
      alert('Selecione um arquivo');
      return;
    }

    const formData = new FormData();
    formData.append('arquivo', arquivo);

    try {
      setImportando(true);
      const response = await api.post('/fichas/api/fichas-tecnicas/importar-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setResultado(response.data);
        if (response.data.erros.length === 0) {
          setTimeout(() => {
            recarregar();
            fecharModal();
          }, 2000);
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Erro ao importar arquivo';
      alert(errorMsg);
      console.error(err);
    } finally {
      setImportando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-2xl font-semibold text-gray-800 mb-1">
                📥 Importar Preparações Base
              </h3>
              <p className="text-sm text-gray-500">
                Importe várias fichas técnicas de uma vez usando Excel
              </p>
            </div>
            <button
              onClick={fecharModal}
              className="text-gray-500 hover:text-gray-700 text-3xl leading-none"
            >
              ×
            </button>
          </div>

          {/* Alerta informativo */}
          <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
            <div className="flex items-start gap-3">
              <FaInfoCircle className="text-blue-600 text-xl flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-2">ℹ️ Sobre a importação:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Apenas fichas do tipo <strong>"Preparação Base"</strong> podem ser importadas</li>
                  <li>Cada aba do Excel representa uma ficha técnica diferente</li>
                  <li>Os ingredientes e embalagens devem estar cadastrados no sistema</li>
                  <li>O template inclui 2 exemplos prontos e 1 ficha em branco</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Instruções visuais */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-3xl mb-2">1️⃣</div>
              <p className="text-sm font-semibold text-gray-700">Baixe o Template</p>
              <p className="text-xs text-gray-500 mt-1">Com exemplos e instruções</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-3xl mb-2">2️⃣</div>
              <p className="text-sm font-semibold text-gray-700">Preencha as Fichas</p>
              <p className="text-xs text-gray-500 mt-1">Uma aba por receita</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-3xl mb-2">3️⃣</div>
              <p className="text-sm font-semibold text-gray-700">Faça o Upload</p>
              <p className="text-xs text-gray-500 mt-1">E importe tudo de uma vez</p>
            </div>
          </div>

          {/* Download Template */}
          <div className="mb-6">
            <button
              onClick={downloadTemplate}
              className="w-full bg-green-600 text-white px-6 py-4 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-3 font-semibold text-lg shadow-md"
            >
              <FaDownload className="text-xl" /> 
              Baixar Template Excel com Exemplos
            </button>
            <p className="text-xs text-gray-500 text-center mt-2">
              O arquivo inclui instruções completas e fichas de exemplo
            </p>
          </div>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500 font-medium">Após preencher o template</span>
            </div>
          </div>

          {/* Upload Area */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Selecionar arquivo preenchido
            </label>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
                dragActive 
                  ? 'border-blue-500 bg-blue-50' 
                  : arquivo
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleArquivoChange}
                className="hidden"
                id="arquivo-upload"
              />
              <label
                htmlFor="arquivo-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <FaFileUpload className={`text-5xl mb-3 ${
                  arquivo ? 'text-green-500' : 'text-gray-400'
                }`} />
                <span className="text-base font-medium text-gray-700 mb-1">
                  {arquivo ? (
                    <>
                      <FaCheckCircle className="inline text-green-500 mr-2" />
                      {arquivo.name}
                    </>
                  ) : (
                    'Clique para selecionar ou arraste o arquivo aqui'
                  )}
                </span>
                <span className="text-sm text-gray-500">
                  Apenas arquivos Excel (.xlsx ou .xls)
                </span>
              </label>
            </div>
          </div>

          {/* Resultado */}
          {resultado && (
            <div className={`mb-6 p-4 rounded-lg border-2 ${
              resultado.erros.length === 0 
                ? 'bg-green-50 border-green-300' 
                : 'bg-yellow-50 border-yellow-300'
            }`}>
              <div className="flex items-start gap-3">
                {resultado.erros.length === 0 ? (
                  <FaCheckCircle className="text-green-600 text-2xl flex-shrink-0 mt-1" />
                ) : (
                  <FaExclamationTriangle className="text-yellow-600 text-2xl flex-shrink-0 mt-1" />
                )}
                <div className="flex-1">
                  <p className={`font-semibold text-lg mb-2 ${
                    resultado.erros.length === 0 ? 'text-green-800' : 'text-yellow-800'
                  }`}>
                    {resultado.message}
                  </p>
                  
                  {resultado.fichas_criadas > 0 && (
                    <p className="text-sm text-gray-700 mb-2">
                      ✅ {resultado.fichas_criadas} preparação(ões) base importada(s) com sucesso
                    </p>
                  )}
                  
                  {resultado.erros.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-semibold text-yellow-800 mb-2">
                        ⚠️ Problemas encontrados ({resultado.erros.length}):
                      </p>
                      <div className="bg-white rounded p-3 max-h-48 overflow-y-auto border border-yellow-200">
                        <ul className="list-none text-sm text-gray-700 space-y-2">
                          {resultado.erros.map((erro, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-yellow-600 font-bold flex-shrink-0">•</span>
                              <span>{erro}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <p className="text-xs text-yellow-700 mt-2">
                        💡 Corrija os problemas no arquivo Excel e tente novamente
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Botões */}
          <div className="flex gap-3">
            <button
              onClick={fecharModal}
              className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold transition-colors"
            >
              {resultado && resultado.erros.length === 0 ? 'Fechar' : 'Cancelar'}
            </button>
            <button
              onClick={handleImportar}
              disabled={!arquivo || importando}
              className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold flex items-center justify-center gap-2 transition-colors shadow-md"
            >
              {importando ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Importando...
                </>
              ) : (
                <>
                  <FaFileUpload /> Importar Fichas
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportarFichasModal;